import React, { useCallback, useEffect, useState } from "react";
import { Truck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const UNASSIGNED = "__none__";
const LOCKED = ["out_for_delivery", "completed", "cancelled"];

export const AssignDriverPanel = ({ order, onChanged }) => {
  const [drivers, setDrivers] = useState([]);
  const [busy, setBusy] = useState(false);
  const canManage = ["owner", "manager", "admin"].includes(order?.role);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/vendor/orders/${order.order_id}/drivers`);
      setDrivers(data.drivers || []);
    } catch { setDrivers([]); }
  }, [order.order_id]);

  useEffect(() => { if (canManage) load(); }, [canManage, load]);

  if (order.fulfillment_type !== "delivery") return null;
  const locked = LOCKED.includes(order.status);

  const assign = async (value) => {
    setBusy(true);
    try {
      await api.post(`/vendor/orders/${order.order_id}/assign-driver`, {
        driver_user_id: value === UNASSIGNED ? null : value,
      });
      toast.success(value === UNASSIGNED ? "Driver removed" : "Driver assigned");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't assign driver");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl bg-violet-500/5 border border-violet-400/30 p-4" data-testid="voc-assign-driver">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-violet-200">
        <Truck className="w-4 h-4" /> Driver
      </div>
      <p className="text-sm mt-1" data-testid="voc-driver-name">{order.driver_name || "Not assigned yet"}</p>
      {canManage && !locked && (
        <select
          disabled={busy}
          value={order.driver_user_id || UNASSIGNED}
          onChange={(e) => assign(e.target.value)}
          className="mt-2 w-full h-10 rounded-lg bg-zinc-900 border border-white/10 px-3 text-sm"
          data-testid="voc-driver-select"
        >
          <option value={UNASSIGNED}>Unassigned</option>
          {drivers.map((d) => (
            <option key={d.user_id} value={d.user_id}>
              {d.name}{d.role === "owner" ? " (owner)" : ""}
            </option>
          ))}
        </select>
      )}
      {canManage && !locked && drivers.length <= 1 && (
        <p className="text-[11px] text-zinc-500 mt-2">Add drivers from your Team page to assign deliveries to them.</p>
      )}
    </div>
  );
};

export default AssignDriverPanel;
